import { SeoAnalyzer } from "@/components/SeoAnalyzer";
import { ProductTable } from "@/components/products/ProductTable";
import { IProduct, ISize, INoteItem, IProductPayload } from "@/components/products/ProductTypes";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X, Upload, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function ProductsPage() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<IProduct | null>(null);
  const [activeTab, setActiveTab] = useState<"basic" | "variants" | "notes" | "gallery" | "seo">(
    "basic",
  );

  // Form States
  const [nameEn, setNameEn] = useState("");
  const [nameBn, setNameBn] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugUserModified, setIsSlugUserModified] = useState(false);

  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };
  const [taglineEn, setTaglineEn] = useState("");
  const [taglineBn, setTaglineBn] = useState("");
  const [descEn, setDescEn] = useState("");
  const [descBn, setDescBn] = useState("");
  const [category, setCategory] = useState<"men" | "women" | "attar" | "unisex">("unisex");
  const [isActive, setIsActive] = useState(true);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [costPrice, setCostPrice] = useState<number | "">("");

  // AEO/SEO States
  const [metaTitleEn, setMetaTitleEn] = useState("");
  const [metaTitleBn, setMetaTitleBn] = useState("");
  const [metaDescEn, setMetaDescEn] = useState("");
  const [metaDescBn, setMetaDescBn] = useState("");
  const [faqs, setFaqs] = useState<
    { question: { en: string; bn: string }; answer: { en: string; bn: string } }[]
  >([]);
  const [badges, setBadges] = useState<string[]>([]);
  const [metaKeywords, setMetaKeywords] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [socialImage, setSocialImage] = useState("");
  const [socialImageFile, setSocialImageFile] = useState<File | null>(null);
  const [socialPreviewUrl, setSocialPreviewUrl] = useState("");
  const [robotsMeta, setRobotsMeta] = useState({
    noindex: false,
    nofollow: false,
    noarchive: false,
  });
  const [focusKeywords, setFocusKeywords] = useState("");

  // Variants State
  const [sizes, setSizes] = useState<ISize[]>([]);

  // Notes State
  const [topNotes, setTopNotes] = useState<INoteItem[]>([]);
  const [heartNotes, setHeartNotes] = useState<INoteItem[]>([]);
  const [baseNotes, setBaseNotes] = useState<INoteItem[]>([]);

  // Images State
  const [featuredImage, setFeaturedImage] = useState("");
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [featuredPreviewUrl, setFeaturedPreviewUrl] = useState("");

  interface IGalleryFileItem {
    file: File;
    preview: string;
  }
  const [galleryItems, setGalleryItems] = useState<(string | IGalleryFileItem)[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Queries
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery<IProduct[]>({
    queryKey: ["products"],
    queryFn: () => apiRequest<IProduct[]>("/products?isAdmin=true"),
  });

  // Mutations — now accept FormData so the server handles Cloudinary
  const createMutation = useMutation({
    mutationFn: (fd: FormData) => apiRequest<IProduct>("/products", "POST", fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product created successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create product");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, fd }: { id: string; fd: FormData }) =>
      apiRequest<IProduct>(`/products/${id}`, "PATCH", fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product updated successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update product");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/products/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete product");
    },
  });

  const resetForm = () => {
    setEditingProduct(null);
    setActiveTab("basic");
    setNameEn("");
    setNameBn("");
    setSlug("");
    setIsSlugUserModified(false);
    setTaglineEn("");
    setTaglineBn("");
    setDescEn("");
    setDescBn("");
    setCategory("unisex");
    setIsActive(true);
    setLowStockThreshold(5);
    setCostPrice("");
    setMetaTitleEn("");
    setMetaTitleBn("");
    setMetaDescEn("");
    setMetaDescBn("");
    setFaqs([]);
    setBadges([]);
    setSizes([
      { ml: 50, price: 1000, stock: 10, sku: `SG-50-${Math.floor(100 + Math.random() * 900)}` },
    ]);
    setTopNotes([]);
    setHeartNotes([]);
    setBaseNotes([]);

    // Reset featured image
    setFeaturedImageFile(null);
    if (featuredPreviewUrl && featuredPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(featuredPreviewUrl);
    }
    setFeaturedPreviewUrl("");
    setFeaturedImage("");

    // Revoke blob URLs in galleryItems before resetting
    galleryItems.forEach((item) => {
      if (typeof item === "object" && item.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
    });
    setGalleryItems([]);

    setSocialImageFile(null);
    if (socialPreviewUrl && socialPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(socialPreviewUrl);
    }
    setSocialPreviewUrl("");
    setSocialImage("");
    setMetaKeywords("");
    setCanonicalUrl("");
    setSocialImage("");
    setRobotsMeta({ noindex: false, nofollow: false, noarchive: false });
    setFocusKeywords("");
  };

  const handleOpenAddModal = () => {
    resetForm();
    setIsOpen(true);
  };

  const handleOpenEditModal = (product: IProduct) => {
    setEditingProduct(product);
    setNameEn(product.name.en);
    setNameBn(product.name.bn);
    setSlug(product.slug || "");
    setIsSlugUserModified(true);
    setTaglineEn(product.tagline.en);
    setTaglineBn(product.tagline.bn);
    setDescEn(product.description?.en || "");
    setDescBn(product.description?.bn || "");
    setCategory(product.category);
    setIsActive(product.isActive);
    setLowStockThreshold(product.lowStockThreshold || 5);
    setCostPrice(product.costPrice ?? "");
    setMetaTitleEn(product.metaTitle?.en || "");
    setMetaTitleBn(product.metaTitle?.bn || "");
    setMetaDescEn(product.metaDescription?.en || "");
    setMetaDescBn(product.metaDescription?.bn || "");
    setFaqs(product.faqs || []);
    setBadges(product.badges || []);
    setSizes(product.sizes || []);
    setMetaKeywords(product.metaKeywords || "");
    setCanonicalUrl(product.canonicalUrl || "");
    setSocialImage(product.socialImage || "");
    setRobotsMeta({
      noindex: !!product.robotsMeta?.noindex,
      nofollow: !!product.robotsMeta?.nofollow,
      noarchive: !!product.robotsMeta?.noarchive,
    });
    setFocusKeywords(product.metaKeywords || "");

    // Notes mappings
    setTopNotes(product.notes?.top || []);
    setHeartNotes(product.notes?.heart || []);
    setBaseNotes(product.notes?.base || []);

    // Set featured and gallery states
    setFeaturedImage(product.images[0] || "");
    setFeaturedPreviewUrl("");
    setFeaturedImageFile(null);

    setGalleryItems(product.images.slice(1) || []);

    setSocialImage(product.socialImage || "");
    setSocialPreviewUrl("");
    setSocialImageFile(null);

    setActiveTab("basic");
    setIsOpen(true);
  };

  // Image Selection / Action helpers
  const handleFeaturedSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFeaturedImageFile(file);
    const localUrl = URL.createObjectURL(file);
    setFeaturedPreviewUrl(localUrl);
  };

  const handleClearFeatured = () => {
    if (featuredPreviewUrl && featuredPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(featuredPreviewUrl);
    }
    setFeaturedImageFile(null);
    setFeaturedPreviewUrl("");
    setFeaturedImage("");
  };

  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newItems: IGalleryFileItem[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      newItems.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }
    setGalleryItems([...galleryItems, ...newItems]);
  };

  const handleRemoveGalleryItem = (index: number) => {
    const item = galleryItems[index];
    if (typeof item === "object" && item.preview.startsWith("blob:")) {
      URL.revokeObjectURL(item.preview);
    }
    setGalleryItems(galleryItems.filter((_, idx) => idx !== index));
  };

  const handleSocialSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSocialImageFile(file);
    const localUrl = URL.createObjectURL(file);
    setSocialPreviewUrl(localUrl);
  };

  const handleClearSocial = () => {
    if (socialPreviewUrl && socialPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(socialPreviewUrl);
    }
    setSocialImageFile(null);
    setSocialPreviewUrl("");
    setSocialImage("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // ── Client-side guards ────────────────────────────────────────────────────
    if (!nameEn.trim() || !nameBn.trim()) {
      toast.error("English and Bengali names are required");
      return;
    }
    if (!taglineEn.trim() || !taglineBn.trim()) {
      toast.error("English and Bengali taglines are required");
      return;
    }
    if (sizes.length === 0) {
      toast.error("Please add at least one size variant");
      return;
    }
    if (!featuredImage && !featuredImageFile) {
      toast.error("A featured product image is required");
      return;
    }

    // ── Build FormData — one request, server handles Cloudinary ───────────────
    const fd = new FormData();

    // Scalar text fields
    fd.append("name", JSON.stringify({ en: nameEn, bn: nameBn }));
    fd.append("slug", slugify(slug || nameEn || "product"));
    fd.append("tagline", JSON.stringify({ en: taglineEn, bn: taglineBn }));
    fd.append("description", JSON.stringify({ en: descEn, bn: descBn }));
    fd.append("category", category);
    fd.append("isActive", String(isActive));
    fd.append("lowStockThreshold", String(lowStockThreshold));
    if (costPrice !== "") fd.append("costPrice", String(costPrice));
    fd.append("metaKeywords", metaKeywords);
    fd.append("canonicalUrl", canonicalUrl);

    // JSON-serialised complex fields
    fd.append("sizes", JSON.stringify(sizes));
    fd.append(
      "notes",
      JSON.stringify({
        top: topNotes.filter((n) => n.name.trim()),
        heart: heartNotes.filter((n) => n.name.trim()),
        base: baseNotes.filter((n) => n.name.trim()),
      }),
    );
    fd.append("badges", JSON.stringify(badges));
    fd.append("metaTitle", JSON.stringify({ en: metaTitleEn, bn: metaTitleBn }));
    fd.append("metaDescription", JSON.stringify({ en: metaDescEn, bn: metaDescBn }));
    fd.append(
      "faqs",
      JSON.stringify(faqs.filter((f) => f.question.en.trim() || f.question.bn.trim())),
    );
    fd.append("robotsMeta", JSON.stringify(robotsMeta));

    // ── Image fields ──────────────────────────────────────────────────────────
    if (featuredImageFile) {
      // New file selected — server uploads to Cloudinary
      fd.append("featuredImage", featuredImageFile);
    } else if (featuredImage) {
      // Already a Cloudinary URL — tell the server to keep it
      fd.append("existingFeaturedImage", featuredImage);
    }

    // Separate new File objects from existing Cloudinary URLs
    const existingGallery: string[] = [];
    for (const item of galleryItems) {
      if (typeof item === "string") {
        existingGallery.push(item);
      } else {
        fd.append("galleryImages", item.file);
      }
    }
    fd.append("existingGalleryImages", JSON.stringify(existingGallery));

    if (socialImageFile) {
      fd.append("socialImage", socialImageFile);
    } else if (socialImage) {
      // Plain string URL — server reads it from req.body
      fd.append("socialImage", socialImage);
    }

    // ── Fire mutation ─────────────────────────────────────────────────────────
    const toastId = toast.loading(editingProduct ? "Saving changes..." : "Creating product...");
    setIsUploading(true);

    const onSettled = () => {
      setIsUploading(false);
      toast.dismiss(toastId);
    };

    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct._id, fd }, { onSettled });
    } else {
      createMutation.mutate(fd, { onSettled });
    }
  };

  // CSV Helpers
  const getApiUrl = (path: string) => {
    const mode = import.meta.env.VITE_API_MODE || "local";
    const base =
      mode === "production"
        ? import.meta.env.VITE_PROD_API_URL || import.meta.env.VITE_API_BASE_URL || ""
        : import.meta.env.VITE_LOCAL_API_URL ||
          import.meta.env.VITE_API_BASE_URL ||
          "http://localhost:5000";
    return base.replace(/\/$/, "") + "/api/v1" + path;
  };

  const exportInventory = async () => {
    try {
      const url = getApiUrl("/products/export-inventory");
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to export inventory");
      const csvText = await res.text();

      const blob = new Blob([csvText], { type: "text/csv" });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `subaashghor-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Inventory CSV exported successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to export inventory");
    }
  };

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const toastId = toast.loading("Updating prices and stock from CSV...");
    try {
      const response = await apiRequest<{ updatedCount: number }>(
        "/products/bulk-update",
        "POST",
        formData,
      );
      toast.success(`Successfully updated ${response.updatedCount} variants!`, { id: toastId });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || "Bulk update failed", { id: toastId });
    } finally {
      e.target.value = ""; // Reset file input
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  return (
    <>
      {!isOpen && (
        <ProductTable
          products={products}
          isLoading={isLoading}
          error={error}
          onAddClick={handleOpenAddModal}
          onEditClick={handleOpenEditModal}
          onDeleteClick={(id, name) => {
            if (window.confirm(`Are you sure you want to delete the product "${name}"?`)) {
              deleteMutation.mutate(id);
            }
          }}
          onExportCSV={exportInventory}
          onImportCSV={handleCSVImport}
        />
      )}

      {/* Add / Edit Product Inline View */}
      {isOpen && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gold/15 pb-4 gap-4">
            <div>
              <h1 className="font-display text-3xl text-gold">
                {editingProduct ? `Edit Fragrance: ${editingProduct.name.en}` : "Add New Fragrance"}
              </h1>
              <p className="text-xs text-muted-foreground mt-1">
                Configure dual-language details, variant matrix pricing, olfactory notes, and
                visibility badges.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-4 py-2 text-sm font-semibold text-gold hover:bg-accent/40 transition cursor-pointer"
            >
              ← Back to Products
            </button>
          </div>

          <div className="w-full rounded-xl border border-gold/30 bg-brand-card p-6 shadow-2xl">
            {/* Tabs Selector */}
            <div className="flex border-b border-gold/15 mb-6">
              {(
                [
                  ["basic", "Basic Info"],
                  ["variants", "Variants Matrix"],
                  ["notes", "Olfactory Journey"],
                  ["gallery", "Images & Gallery"],
                  ["seo", "AEO & SEO Engines"],
                ] as const
              ).map(([tabId, label]) => (
                <button
                  key={tabId}
                  type="button"
                  onClick={() => setActiveTab(tabId)}
                  className={`border-b-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider -mb-px transition ${
                    activeTab === tabId
                      ? "border-gold text-gold"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tab 1: Basic Info */}
              {activeTab === "basic" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Name (English) *
                      </label>
                      <input
                        type="text"
                        required
                        value={nameEn}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNameEn(val);
                          if (!isSlugUserModified && !editingProduct) {
                            setSlug(slugify(val));
                          }
                        }}
                        placeholder="Oud Royale"
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Name (Bengali) *
                      </label>
                      <input
                        type="text"
                        required
                        value={nameBn}
                        onChange={(e) => setNameBn(e.target.value)}
                        placeholder="উদ রয়্যাল"
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      />
                    </div>
                  </div>

                  {/* URL Slug (Permalink) */}
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                      URL Slug (Permalink / Link ID) *
                    </label>
                    <div className="flex items-center rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground focus-within:border-gold">
                      <span className="text-muted-foreground text-xs select-none pr-1 font-mono">/products/</span>
                      <input
                        type="text"
                        required
                        value={slug}
                        onChange={(e) => {
                          setIsSlugUserModified(true);
                          setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
                        }}
                        placeholder="e.g. oud-royale"
                        className="w-full bg-transparent text-sm text-foreground outline-none font-mono"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Public storefront URL: <span className="text-gold font-mono">https://subaashghor.com/products/{slug || "your-custom-slug"}</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Short Tagline (English) *
                      </label>
                      <input
                        type="text"
                        required
                        value={taglineEn}
                        onChange={(e) => setTaglineEn(e.target.value)}
                        placeholder="e.g. Elegant blend of precious oud and rose"
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Short Tagline (Bengali) *
                      </label>
                      <input
                        type="text"
                        required
                        value={taglineBn}
                        onChange={(e) => setTaglineBn(e.target.value)}
                        placeholder="e.g. মূল্যবান উদ এবং গোলাপের মার্জিত মিশ্রণ"
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Detailed Description (English)
                      </label>
                      <textarea
                        rows={3}
                        value={descEn}
                        onChange={(e) => setDescEn(e.target.value)}
                        placeholder="Detailed aroma breakdown..."
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Detailed Description (Bengali)
                      </label>
                      <textarea
                        rows={3}
                        value={descBn}
                        onChange={(e) => setDescBn(e.target.value)}
                        placeholder="বিস্তারিত সুগন্ধি বিবরণ..."
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold resize-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Category *
                      </label>
                      <select
                        value={category}
                        onChange={(e: any) => setCategory(e.target.value)}
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      >
                        <option value="men">Men</option>
                        <option value="women">Women</option>
                        <option value="attar">Attar</option>
                        <option value="unisex">Unisex</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-wider text-amber-400 mb-1 font-semibold">
                        Cost Price (BDT) 🔒
                      </label>
                      <input
                        type="number"
                        value={costPrice}
                        onChange={(e) => setCostPrice(e.target.value === "" ? "" : Number(e.target.value))}
                        placeholder="e.g. 450"
                        className="w-full rounded border border-amber-500/30 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-amber-400"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                        Low Stock Alert Level *
                      </label>
                      <input
                        type="number"
                        required
                        value={lowStockThreshold}
                        onChange={(e) => setLowStockThreshold(Number(e.target.value))}
                        placeholder="5"
                        className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                      />
                    </div>

                    <div className="flex flex-col justify-end pb-1.5">
                      <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold-soft cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="accent-gold h-4 w-4"
                        />
                        <span>Visible on Store (Active)</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                      Visibility Badges
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {["best-seller", "new", "limited-edition"].map((badge) => {
                        const hasBadge = badges.includes(badge);
                        return (
                          <button
                            key={badge}
                            type="button"
                            onClick={() => {
                              if (hasBadge) {
                                setBadges(badges.filter((b) => b !== badge));
                              } else {
                                setBadges([...badges, badge]);
                              }
                            }}
                            className={`rounded-full px-4 py-1.5 text-xs font-semibold border capitalize transition ${
                              hasBadge
                                ? "bg-gold text-primary-foreground border-gold"
                                : "border-gold/20 text-foreground/80 hover:border-gold/50"
                            }`}
                          >
                            {badge.replace("-", " ")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Variants Matrix */}
              {activeTab === "variants" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-semibold text-gold-soft uppercase tracking-wider">
                      Multi-Size Pricing Matrix
                    </h3>
                    <button
                      type="button"
                      onClick={() =>
                        setSizes([
                          ...sizes,
                          { ml: 50, price: 0, stock: 0, sku: `SG-VAR-${Date.now()}` },
                        ])
                      }
                      className="inline-flex items-center gap-1.5 rounded bg-gold/10 border border-gold/30 px-3 py-1.5 text-xs text-gold hover:bg-gold/20 transition cursor-pointer"
                    >
                      <Plus className="h-3 w-3" /> Add Variant
                    </button>
                  </div>

                  <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                    {sizes.length === 0 ? (
                      <div className="text-center py-6 text-xs text-muted-foreground">
                        No size variants configured. Please add at least one variant.
                      </div>
                    ) : (
                      sizes.map((s, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-6 gap-2 items-center rounded border border-gold/15 bg-accent/5 p-2"
                        >
                          <div>
                            <label className="block text-[10px] text-muted-foreground uppercase mb-0.5">
                              Size (ml) *
                            </label>
                            <select
                              value={s.ml}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].ml = Number(e.target.value);
                                setSizes(newSizes);
                              }}
                              className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            >
                              <option value={3}>3 ml</option>
                              <option value={6}>6 ml</option>
                              <option value={12}>12 ml</option>
                              <option value={30}>30 ml</option>
                              <option value={50}>50 ml</option>
                              <option value={100}>100 ml</option>
                              <option value={200}>200 ml</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-[10px] text-muted-foreground uppercase mb-0.5">
                              Reg. Price *
                            </label>
                            <input
                              type="number"
                              required
                              value={s.price || ""}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].price = Number(e.target.value);
                                setSizes(newSizes);
                              }}
                              placeholder="1200"
                              className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] text-muted-foreground uppercase mb-0.5">
                              Sale Price
                            </label>
                            <input
                              type="number"
                              value={s.salePrice || ""}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].salePrice =
                                  e.target.value === "" ? undefined : Number(e.target.value);
                                setSizes(newSizes);
                              }}
                              placeholder="950"
                              className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] text-amber-400 uppercase mb-0.5 font-medium">
                              Cost Price 🔒
                            </label>
                            <input
                              type="number"
                              value={s.costPrice || ""}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].costPrice =
                                  e.target.value === "" ? undefined : Number(e.target.value);
                                setSizes(newSizes);
                              }}
                              placeholder="450"
                              className="w-full rounded border border-amber-500/30 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-amber-400"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] text-muted-foreground uppercase mb-0.5">
                              Stock *
                            </label>
                            <input
                              type="number"
                              required
                              value={s.stock}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].stock = Number(e.target.value);
                                setSizes(newSizes);
                              }}
                              placeholder="15"
                              className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            />
                          </div>

                          <div className="flex items-center gap-1.5 pt-4">
                            <input
                              type="text"
                              value={s.sku || ""}
                              onChange={(e) => {
                                const newSizes = [...sizes];
                                newSizes[index].sku = e.target.value;
                                setSizes(newSizes);
                              }}
                              placeholder="SKU"
                              className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none font-mono"
                            />
                            <button
                              type="button"
                              onClick={() => setSizes(sizes.filter((_, idx) => idx !== index))}
                              className="rounded p-1 text-red-400 hover:bg-red-500/10 cursor-pointer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Tab 3: Olfactory Journey */}
              {activeTab === "notes" && (
                <div className="space-y-4">
                  {(
                    [
                      ["top", "Top Notes (🍃)", topNotes, setTopNotes],
                      ["heart", "Heart Notes (🌹)", heartNotes, setHeartNotes],
                      ["base", "Base Notes (🪵)", baseNotes, setBaseNotes],
                    ] as const
                  ).map(([level, title, list, setList]) => (
                    <div key={level} className="rounded border border-gold/15 bg-accent/5 p-3">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-semibold text-gold uppercase tracking-wider">
                          {title}
                        </h4>
                        <button
                          type="button"
                          onClick={() => setList([...list, { name: "", icon: "" }])}
                          className="text-[10px] text-gold hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Plus className="h-3 w-3" /> Add note
                        </button>
                      </div>

                      <div className="space-y-2">
                        {list.map((note, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <input
                              type="text"
                              required
                              placeholder="Note name (e.g. Bergamot)"
                              value={note.name}
                              onChange={(e) => {
                                const newList = [...list];
                                newList[index].name = e.target.value;
                                setList(newList);
                              }}
                              className="flex-1 rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            />
                            <input
                              type="text"
                              placeholder="Icon URL (Optional)"
                              value={note.icon || ""}
                              onChange={(e) => {
                                const newList = [...list];
                                newList[index].icon = e.target.value;
                                setList(newList);
                              }}
                              className="flex-1 rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => setList(list.filter((_, idx) => idx !== index))}
                              className="text-red-400 hover:bg-red-500/10 p-1 rounded cursor-pointer"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tab 4: Images & Gallery */}
              {activeTab === "gallery" && (
                <div className="space-y-6">
                  {/* Featured Main Image */}
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                      Featured Main Image *
                    </label>
                    {featuredPreviewUrl || featuredImage ? (
                      <div className="relative rounded-lg border border-gold/30 bg-accent/10 p-2 flex items-center gap-3">
                        <img
                          src={featuredPreviewUrl || featuredImage}
                          alt="Preview"
                          className="h-16 w-16 rounded object-cover border border-gold/25"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-foreground truncate">
                            {featuredImageFile ? featuredImageFile.name : "Featured Main Image"}
                          </div>
                          <div className="text-[10px] text-gold-soft">
                            {featuredImage
                              ? "Uploaded to Cloudinary"
                              : "Pending save (Local preview)"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearFeatured}
                          className="rounded bg-red-500/10 border border-red-500/30 p-1.5 text-red-300 hover:bg-red-500/25 transition cursor-pointer"
                          title="Remove image"
                          disabled={isUploading}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative flex flex-col items-center justify-center border-2 border-dashed border-gold/30 rounded-lg p-6 bg-accent/5 hover:bg-accent/10 transition">
                        <label className="flex flex-col items-center cursor-pointer">
                          <Upload className="h-8 w-8 text-gold-soft mb-2" />
                          <span className="text-xs text-foreground font-medium">
                            Click to select featured image
                          </span>
                          <span className="text-[10px] text-muted-foreground mt-1">
                            Supports JPG, PNG or WEBP (Max 5MB)
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFeaturedSelect}
                          />
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Gallery Images Grid */}
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                      Gallery Images
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                      {galleryItems.map((item, idx) => {
                        const src = typeof item === "string" ? item : item.preview;
                        const name = typeof item === "string" ? "Uploaded Image" : item.file.name;
                        return (
                          <div
                            key={idx}
                            className="relative group aspect-square rounded-lg overflow-hidden border border-gold/20 bg-accent/5"
                          >
                            <img src={src} alt="" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                              <span className="text-[9px] text-white/90 truncate">{name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveGalleryItem(idx)}
                                className="self-end rounded bg-red-500/25 border border-red-500/50 p-1 text-red-300 hover:bg-red-500/50 transition cursor-pointer"
                                title="Delete image"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Add Gallery Item Card */}
                      <label className="relative flex flex-col items-center justify-center aspect-square border-2 border-dashed border-gold/30 rounded-lg bg-accent/5 hover:bg-accent/10 transition cursor-pointer">
                        <Plus className="h-6 w-6 text-gold-soft mb-1" />
                        <span className="text-[10px] text-foreground font-medium">Add Photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleGallerySelect}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 5: AEO & SEO Engines */}
              {/* Tab 5: AEO & SEO Engines */}
              {activeTab === "seo" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left columns (2/3 width on large screens) */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Meta Title */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Title (English)
                        </label>
                        <input
                          type="text"
                          value={metaTitleEn}
                          onChange={(e) => setMetaTitleEn(e.target.value)}
                          placeholder="Defaults to product name"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Title (Bengali)
                        </label>
                        <input
                          type="text"
                          value={metaTitleBn}
                          onChange={(e) => setMetaTitleBn(e.target.value)}
                          placeholder="Defaults to product name"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Meta Description */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Description (English)
                        </label>
                        <textarea
                          rows={3}
                          value={metaDescEn}
                          onChange={(e) => setMetaDescEn(e.target.value)}
                          placeholder="Defaults to description/tagline"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Meta Description (Bengali)
                        </label>
                        <textarea
                          rows={3}
                          value={metaDescBn}
                          onChange={(e) => setMetaDescBn(e.target.value)}
                          placeholder="Defaults to description/tagline"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Focus Keywords & Canonical URL */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Focus Keywords (Up to 5, comma-separated)
                        </label>
                        <input
                          type="text"
                          value={focusKeywords}
                          onChange={(e) => {
                            setFocusKeywords(e.target.value);
                            setMetaKeywords(e.target.value);
                          }}
                          placeholder="e.g. oud, perfume, royal"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Canonical URL
                        </label>
                        <input
                          type="text"
                          value={canonicalUrl}
                          onChange={(e) => setCanonicalUrl(e.target.value)}
                          placeholder="e.g. https://subaashghor.com/products/oud-royale"
                          className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                        />
                      </div>
                    </div>

                    {/* Social OG Image & Robots Flags */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                          Social Image (OG Image Override)
                        </label>
                        {socialPreviewUrl || socialImage ? (
                          <div className="relative rounded-lg border border-gold/30 bg-accent/10 p-2 flex items-center gap-3">
                            <img
                              src={socialPreviewUrl || socialImage}
                              alt="Preview"
                              className="h-16 w-28 rounded object-cover border border-gold/25"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-foreground truncate">
                                {socialImageFile ? socialImageFile.name : "Social Share Image"}
                              </div>
                              <div className="text-[10px] text-gold-soft">
                                {socialImage
                                  ? "Uploaded to Cloudinary"
                                  : "Pending save (Local preview)"}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleClearSocial}
                              className="rounded bg-red-500/10 border border-red-500/30 p-1.5 text-red-300 hover:bg-red-500/25 transition cursor-pointer"
                              title="Remove image"
                              disabled={isUploading}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="relative flex flex-col items-center justify-center border-2 border-dashed border-gold/30 rounded-lg p-6 bg-accent/5 hover:bg-accent/10 transition">
                            <label className="flex flex-col items-center cursor-pointer">
                              <Upload className="h-6 w-6 text-gold-soft mb-1" />
                              <span className="text-xs text-foreground font-medium">
                                Click to select social image
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleSocialSelect}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-gold-soft mb-2">
                          Robots Meta Settings
                        </label>
                        <div className="space-y-2 border border-gold/15 bg-accent/5 p-3 rounded">
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.noindex}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, noindex: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Noindex (Do not display this page in search results)</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.nofollow}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, nofollow: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Nofollow (Do not follow links on this page)</span>
                          </label>
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={robotsMeta.noarchive}
                              onChange={(e) =>
                                setRobotsMeta({ ...robotsMeta, noarchive: e.target.checked })
                              }
                              className="rounded border-gold/25 text-gold focus:ring-gold bg-accent/20 h-4 w-4"
                            />
                            <span>Noarchive (Do not show cached link in search results)</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* FAQs */}
                    <div className="border-t border-gold/15 pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-xs uppercase tracking-wider text-gold-soft">
                          Structured FAQs & FAQPage Schema
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setFaqs([
                              ...faqs,
                              { question: { en: "", bn: "" }, answer: { en: "", bn: "" } },
                            ])
                          }
                          className="text-xs text-gold hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add FAQ
                        </button>
                      </div>

                      {faqs.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">
                          No custom FAQs defined. Site fallback FAQs will be used.
                        </p>
                      ) : (
                        <div className="space-y-4 max-h-[200px] overflow-y-auto pr-2 border border-gold/10 p-3 rounded-lg bg-accent/5">
                          {faqs.map((faq, idx) => (
                            <div
                              key={idx}
                              className="border-b border-gold/10 pb-4 last:border-0 last:pb-0 space-y-3 relative pr-8"
                            >
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Question (English)
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={faq.question.en}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].question.en = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Question (Bengali)
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    value={faq.question.bn}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].question.bn = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Answer (English)
                                  </label>
                                  <textarea
                                    rows={2}
                                    required
                                    value={faq.answer.en}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].answer.en = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wider text-gold-soft mb-0.5">
                                    Answer (Bengali)
                                  </label>
                                  <textarea
                                    rows={2}
                                    required
                                    value={faq.answer.bn}
                                    onChange={(e) => {
                                      const newFaqs = [...faqs];
                                      newFaqs[idx].answer.bn = e.target.value;
                                      setFaqs(newFaqs);
                                    }}
                                    className="w-full rounded border border-gold/25 bg-accent/25 px-2 py-1 text-xs text-foreground outline-none focus:border-gold"
                                  />
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => setFaqs(faqs.filter((_, i) => i !== idx))}
                                className="absolute right-0 top-1/2 -translate-y-1/2 text-red-400 hover:bg-red-500/10 p-1 rounded cursor-pointer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column: SEO Realtime Analyzer */}
                  <div className="border border-gold/15 bg-accent/5 p-4 rounded-lg h-fit space-y-4">
                    <SeoAnalyzer
                      title={metaTitleEn || nameEn}
                      description={metaDescEn || taglineEn}
                      content={descEn}
                      focusKeywords={focusKeywords}
                      canonicalUrl={canonicalUrl}
                      slug={slug || slugify(nameEn || "product")}
                      folderPath="products"
                    />
                  </div>
                </div>
              )}

              {/* Actions Footer */}
              <div className="pt-6 border-t border-gold/15 flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  * Fields are required in their respective tabs.
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    disabled={isUploading || createMutation.isPending || updateMutation.isPending}
                    className="rounded border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-accent/40 cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUploading || createMutation.isPending || updateMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                  >
                    {(isUploading || createMutation.isPending || updateMutation.isPending) && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                    Save Product
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
